
import { Observable, Subject  } from 'rxjs' 
import { map,mapTo,catchError,debounceTime,distinctUntilChanged } from 'rxjs/operators';

import * as Enumerable from "linq"
import _ from "lodash"
import Big from "big.js"

import { formatDate } from 'element-ui/src/utils/date-util'

/*
 * # 如果计算阶段是7天，那么这里默认必须传入 7*24 + 1 小时的数据( 由外部查询控制 )。比如 8-6 到 8-12。需要传入 8-5 23:00 到 8-13 00:00的数据。
 * 因为 8-6 00:00 这时间点上的数据是用来描述 ( 8-5 23:00 到 8-6 00:00 之前的能耗数据。某些数据需要用这2个时间段的数据做出差值来描述。 )
 *
 * 1. PUE和WBT是取每小时的数据，整合后，除以 7*24 小时。这个时候取的数据是 第一天 00:00 和 第7天 23：00的数据
 * 2. 其他的平均值都是取 ( 最后一条记录 - 第一条记录 ) / ( 7*24 ). 那么这里最后一条记录需要是 第8天 00:00的数据( 第7天24:00的数据。 )
 * 3. 如果PUE > 1 , < 3 , 那么这条记录在计算PUE，WBT 和 IT负载率的时候会被忽略掉。
 *
 * source Data , 传入的每天小时数据
 *
 * 这里的3个参数必须在外部初始化完毕后传入。
 * hourDataObject, 用于返回经过整理的每天小时数据。排序( 如果必要，增加一个结尾数据，比如查8-6到8-12，增加一条 8-13 00:00 的数据 )
 * totalDataObject, 用于返回经过整理的加总数据。
 * averageDailyDataObject, 用于返回经过整理的加总平均。
 *
 *
 * startTime，endTime是精确到毫秒的报表起止日期的Date.getTime()。
 *    例如: 起止 4-1 到 4-7 那么传入 ( new Date( '2019-04-01 00:00:00' ) ).getTime(), ( new Date( '2019-04-07 23:59:59' ) ).getTime()
 */
function massagePueData( sourceData, hourDataObject, totalDataObject, averageDailyDataObject, startTime, endTime, otherProps ){
    
    averageDailyDataObject.tsdateStart = new Date ( startTime );
    averageDailyDataObject.tsdateEnd   = new Date ( endTime   );
    
    if( sourceData.length <= 0 ){
        return;
    }
    
               
    let pueRecordIndex = 0;
    
    let pue                        = new Big(0); // PUE值                 
    let recordRoom                 = new Big(0); // 时间段内总能耗
    let recordIT                   = new Big(0); // 时间段内总IT能耗
    let coldSource                 = new Big(0); // 冷源
    let coldTerminal               = new Big(0); // 末端
    
    let upsIn                      = new Big(0); // ups 输入
    let upsOut                     = new Big(0); // ups 输出
    let trIn                       = new Big(0); // 变压器输入
    let trOut                      = new Big(0); // 变压器输出
    let hvdcIn                     = new Big(0); // 高压直流输入
    let hvdcOut                    = new Big(0); // 高压直流输出
                        
    let distributionLoss           = new Big(0);   // 配电损耗 %  
    let otherCost                  = new Big(0);
    
    let wbt                        = new Big(0); // 湿球温度
    let itLoadRate                 = new Big(0);
    
    let waterCost                  = new Big(0);
    let waterCostAmount            = new Big(0); //每小时耗水量
    
    Enumerable.from( sourceData ).forEach(
        ( item ) => {
            if( item.tsdate ){
                let tsdate = new Date( item.tsdate );
                let hour   = item.recordpointId     ;
                tsdate = new Date( tsdate.getFullYear(), tsdate.getMonth() , tsdate.getDate() , hour, 0, 0, 0 ); 
                item.dataTime = tsdate;         
            }else{
                item.dataTime = 0;
            }
        } 
    );
    
    let orderedDailyData = 
        Enumerable
        .from( sourceData )
        .where( (item)=>{ return ( item.dataTime && item.dataTime.getTime() < endTime.getTime() && item.dataTime.getTime() >=startTime.getTime() ); } )
        .orderBy( (item)=>{ return item.dataTime.getTime() } )
        .toArray();
    
    /* 一般会多传一小时数据进来，这里就把这部分数据提取出来。 */    
    let outOfEndTimeData = 
        Enumerable
        .from( sourceData )
        .where( 
            (item)=>{ 
                return ( item.dataTime >= endTime);
            } 
        )
        .orderBy( (item)=>{ return item.dataTime.getTime() } )
        .toArray();
        
    let outOfStartTimeData = 
        Enumerable
        .from( sourceData )
        .where( (item)=>{ return (item.tsdate && item.tsdate < startTime); } )
        .orderBy( (item)=>{ return item.dataTime.getTime() } )
        .toArray();

    Enumerable.from( orderedDailyData ).forEach(
        ( item ) => {
            if( item.puePhase && item.puePhase > 1 && item.puePhase < 3 ){
                pueRecordIndex ++;
                pue         = pue.plus          ( item.puePhase    ? item.puePhase    : 0 )     ;
                itLoadRate  = itLoadRate.plus   ( item.itLoadRate  ? item.itLoadRate  : 0 )     ;
                wbt         = wbt.plus          ( item.wbt         ? item.wbt         : 0 )     ;               
            }
        } 
    );

    /* 取第一天和最后一天数据，总能耗数据一般直接由这2个数据的差值算出 */
    let firstDailyData = orderedDailyData[0];
    let lastDailyData  = orderedDailyData[ ( orderedDailyData.length - 1 ) ];
    if( outOfEndTimeData.length > 0 ){
        lastDailyData = outOfEndTimeData[0];
    }
    if( outOfStartTimeData.length > 0 ){
        firstDailyData = outOfStartTimeData[0];
    }
    
    let timeDiff = lastDailyData.tsdate - firstDailyData.tsdate;
    let hours = Math.floor( timeDiff / 3600000) + lastDailyData.recordpointId - firstDailyData.recordpointId ;
    if( hours <= 0 ) hours = 1;
    

    recordRoom         = new Big( lastDailyData.recordRoom   - firstDailyData.recordRoom     );
    recordIT           = new Big( lastDailyData.recordIT     - firstDailyData.recordIT       );
    coldSource         = new Big( lastDailyData.coldSource   - firstDailyData.coldSource     );
    coldTerminal       = new Big( lastDailyData.coldTerminal - firstDailyData.coldTerminal   );
    upsIn              = new Big( lastDailyData.upsIn        - firstDailyData.upsIn          );
    upsOut             = new Big( lastDailyData.upsOut       - firstDailyData.upsOut         );
    trIn               = new Big( lastDailyData.trIn         - firstDailyData.trIn           );
    trOut              = new Big( lastDailyData.trOut        - firstDailyData.trOut          );
    hvdcIn             = new Big( lastDailyData.hvdcIn       - firstDailyData.hvdcIn         );
    hvdcOut            = new Big( lastDailyData.hvdcOut      - firstDailyData.hvdcOut        );
    otherCost          = new Big( lastDailyData.otherCost    - firstDailyData.otherCost      );
    waterCost          = ( new Big( lastDailyData.waterCost    - firstDailyData.waterCost      ) ).div( 1000 ); //系统抄表值单位是千克，显示的是顿，这里直接转换

    distributionLoss = upsIn.minus( upsOut ).plus( trIn.minus( trOut ) ).plus( hvdcIn.minus( hvdcOut ) );
    otherCost = recordRoom.minus( coldSource ).minus(coldTerminal).minus(distributionLoss).minus(recordIT);
        
    let averagePUE                  = new Big(0)  ;
    let averageRecordRoom           = new Big(0)  ;
    let averageRecordIT             = new Big(0)  ;
    let averageColdSource           = new Big(0)  ;
    let averageColdTerminal         = new Big(0)  ;
    let averageUpsIn                = new Big(0)  ;
    let averageUpsOut               = new Big(0)  ;
    let averageTrIn                 = new Big(0)  ;
    let averageTrOut                = new Big(0)  ;
    let averageHvdcIn               = new Big(0)  ;
    let averageHvdcOut              = new Big(0)  ;
    let averageWBT                  = new Big(0)  ;
    let averageITLoadRate           = new Big(0)  ;
      
    let averageOtherCost            = new Big(0)  ;
    let averageDistributionLoss     = new Big(0)  ;

    let averageWaterCost            = new Big(0)  ;          
                      
    if( pueRecordIndex > 0 && hours > 2 ){
    
        averagePUE                  = pue.div              ( pueRecordIndex          )  ;      
        averageRecordRoom           = recordRoom.div       ( new Big( hours )        )  ;
        averageRecordIT             = recordIT.div         ( new Big( hours )        )  ;
        averageColdSource           = coldSource.div       ( new Big( hours )        )  ;    
        averageColdTerminal         = coldTerminal.div     ( new Big( hours )        )  ;  
        averageUpsIn                = upsIn.div            ( new Big( hours )        )  ;         
        averageUpsOut               = upsOut.div           ( new Big( hours )        )  ;        
        averageTrIn                 = trIn.div             ( new Big( hours )        )  ;          
        averageTrOut                = trOut.div            ( new Big( hours )        )  ;         
        averageHvdcIn               = hvdcIn.div           ( new Big( hours )        )  ;         
        averageHvdcOut              = hvdcOut.div          ( new Big( hours )        )  ; 
        averageWBT                  = wbt.div              ( pueRecordIndex          )  ;
        averageITLoadRate           = itLoadRate.div       ( pueRecordIndex          )  ;

        
        //配电损耗=（UPS总输入-UPS总输出）+（变压器输入-变压器输出）+（高压直流输入-高压直流输出）
        averageDistributionLoss     = distributionLoss.div ( pueRecordIndex          )  ;
        averageWaterCost            = waterCost.div        ( pueRecordIndex )  ;
    }else {
        averagePUE                  = lastDailyData.puePhase     ;      
        averageRecordRoom           = recordRoom                 ;
        averageRecordIT             = recordIT                   ;
        averageColdSource           = coldSource                 ;    
        averageColdTerminal         = coldTerminal               ;  
        averageUpsIn                = upsIn                      ;         
        averageUpsOut               = upsOut                     ;        
        averageTrIn                 = trIn                       ;          
        averageTrOut                = trOut                      ;         
        averageHvdcIn               = hvdcIn                     ;         
        averageHvdcOut              = hvdcOut                    ; 
        averageWBT                  = new Big( lastDailyData.wbt        )  ;
        averageITLoadRate           = new Big( lastDailyData.itLoadRate )  ;

        //配电损耗=（UPS总输入-UPS总输出）+（变压器输入-变压器输出）+（高压直流输入-高压直流输出）
        averageDistributionLoss     = distributionLoss ;
        averageWaterCost            = waterCost        ;
    }
    averageOtherCost = averageRecordRoom.minus( averageColdSource ).minus(averageColdTerminal).minus(averageDistributionLoss).minus(averageRecordIT);
    
    
    if( otherProps && otherProps.itLoad ){
        averageITLoadRate = averageRecordIT.times( new Big(100) ).div( new Big( otherProps.itLoad )  );
    }
    
    averageDailyDataObject.puePhase          =  averagePUE                ; 
    averageDailyDataObject.recordRoom        =  averageRecordRoom         ; 
    averageDailyDataObject.recordIT          =  averageRecordIT           ; 
    averageDailyDataObject.coldSource        =  averageColdSource         ; 
    averageDailyDataObject.coldTerminal      =  averageColdTerminal       ; 
    averageDailyDataObject.upsIn             =  averageUpsIn              ; 
    averageDailyDataObject.upsOut            =  averageUpsOut             ; 
    averageDailyDataObject.trIn              =  averageTrIn               ; 
    averageDailyDataObject.trOut             =  averageTrOut              ; 
    averageDailyDataObject.hvdcIn            =  averageHvdcIn             ; 
    averageDailyDataObject.hvdcOut           =  averageHvdcOut            ;
    averageDailyDataObject.wbt               =  averageWBT                ;
    averageDailyDataObject.itLoadRate        =  averageITLoadRate         ;
    averageDailyDataObject.waterCost         =  averageWaterCost          ; 
    averageDailyDataObject.otherCost         =  averageOtherCost          ;
    averageDailyDataObject.distributionLoss  =  averageDistributionLoss   ;
    
    
    totalDataObject.puePhase          =  averagePUE        ;
    totalDataObject.recordRoom        =  recordRoom        ;
    totalDataObject.recordIT          =  recordIT          ;
    totalDataObject.coldSource        =  coldSource        ;
    totalDataObject.coldTerminal      =  coldTerminal      ;
    totalDataObject.upsIn             =  upsIn             ;
    totalDataObject.upsOut            =  upsOut            ;
    totalDataObject.trIn              =  trIn              ;
    totalDataObject.trOut             =  trOut             ;
    totalDataObject.hvdcIn            =  hvdcIn            ;
    totalDataObject.hvdcOut           =  hvdcOut           ;
    totalDataObject.wbt               =  averageWBT        ;
    totalDataObject.itLoadRate        =  averageITLoadRate ;                                                                 
    totalDataObject.waterCost         =  waterCost         ;     
    totalDataObject.otherCost         =  otherCost         ;                                                               
    totalDataObject.distributionLoss  =  distributionLoss  ;
    
    hourDataObject.push( ...orderedDailyData );
    if( outOfEndTimeData.length > 0 ){
        hourDataObject.push( outOfEndTimeData[0] );
    }
}

function massageSubsysData( sourceData, hourDataObject, subsysData, startTime, endTime ){
    
    subsysData.tsdateStart = new Date ( startTime );
    subsysData.tsdateEnd   = new Date ( endTime   );
    
    if( sourceData.length <= 0 ){
        return;
    }
               
    let recordIndex = 0;
    
    let total    = new Big(0); 
    let average  = new Big(0);               
    
    Enumerable.from( sourceData ).forEach(
        ( item ) => {
            if( item.tsdate ){
                let tsdate = new Date( item.tsdate );
                let hour   = item.recordpoint     ;
                tsdate = new Date( tsdate.getFullYear(), tsdate.getMonth() , tsdate.getDate() , hour, 0, 0, 0 ); 
                item.dataTime = tsdate;         
            }else{
                item.dataTime = 0;
            }
        } 
    );
    
    let orderedDailyData = 
        Enumerable
        .from( sourceData )
        .where( (item)=>{ return ( item.dataTime && item.dataTime.getTime() < endTime.getTime() && item.dataTime.getTime() >=startTime.getTime() ); } )
        .orderBy( (item)=>{ return item.dataTime.getTime() } )
        .toArray();
    
    /* 一般会多传一小时数据进来，这里就把这部分数据提取出来。 */    
    let outOfEndTimeData = 
        Enumerable
        .from( sourceData )
        .where( 
            (item)=>{ 
                return ( item.dataTime >= endTime);
            } 
        )
        .orderBy( (item)=>{ return item.dataTime.getTime() } )
        .toArray();

    /* 取第一天和最后一天数据，总数据一般直接由这2个数据的差值算出 */
    let firstDailyData = orderedDailyData[0];
    let lastDailyData  = orderedDailyData[ ( orderedDailyData.length - 1 ) ];
    if( outOfEndTimeData.length > 0 ){
        lastDailyData = outOfEndTimeData[0];
    }
    
    let timeDiff = lastDailyData.tsdate - firstDailyData.tsdate;
    let hours = Math.floor( timeDiff / 3600000) + lastDailyData.recordpoint - firstDailyData.recordpoint ;
    if( hours <= 0 ) hours = 1;
    

    total  = new Big( lastDailyData.subsysTotalRecord ).minus( firstDailyData.subsysTotalRecord );
    
    average = new Big( _.get( firstDailyData , "subsysHourRecord", 0 ) );               
    if( orderedDailyData.length > 0 ){
        average = total.div( new Big( hours ) )  ;      
    }
    
    subsysData.subsysAmout        =  average  ; 
    subsysData.subsysTotalRecord  =  total    ; 
    
    hourDataObject.push( ...orderedDailyData );
    if( outOfEndTimeData.length > 0 ){
        hourDataObject.push( outOfEndTimeData[0] );
    }
}

function hourDataSimilarRanking( hoursData, allSimilarWorkCondition, similarRange, rankPropertyPath ){
    
    let hourDataSimilarWorkConditionRanks = [];
    
    for( let key in hoursData ){
    
        let oneHourData = hoursData[key];
        
        let oneHourSimilarCondition = {};
        
        try{
            oneHourSimilarCondition = {
                maxItLoadRate : ( ( new Big( oneHourData.itLoadRate ) ).plus ( new Big(  similarRange.itLoadRateRange ) ) ).toFixed(2),
                minItLoadRate : ( ( new Big( oneHourData.itLoadRate ) ).minus( new Big(  similarRange.itLoadRateRange ) ) ).toFixed(2),
                maxWbt : ( ( new Big( oneHourData.wbt ) ).plus  ( new Big(  similarRange.wbtRange ) ) ).toFixed(2),
                minWbt : ( ( new Big( oneHourData.wbt ) ).minus ( new Big(  similarRange.wbtRange ) ) ).toFixed(2)
            };
        }catch(error){
            console.log( error );
        }
    
        let oneHourSimilar = Enumerable
            .from( allSimilarWorkCondition )
            .where( 
                (item)=>{ 
                    return (
                        (
                            item.itLoadRate 
                            && item.itLoadRate >= oneHourSimilarCondition.minItLoadRate 
                            && item.itLoadRate <= oneHourSimilarCondition.maxItLoadRate 
                        )&& (
                            item.itLoadRate 
                            && item.wbt >= oneHourSimilarCondition.minWbt 
                            && item.wbt <= oneHourSimilarCondition.maxWbt 
                        )
                    ); 
                } 
             )
            .toArray();
            
        if( oneHourSimilar ){
            let index = oneHourSimilar.findIndex( 
                ( item ) => { 
                    return ( item.tsdate == oneHourData.tsdate && item.recordpointId == oneHourData.recordpointId );
                } 
            );
            
            if( index < 0 ){
               oneHourSimilar.push( oneHourData );
            }
            
            oneHourSimilar = Enumerable
                .from( oneHourSimilar )
                .orderBy( (item)=>{ return _.get( item , rankPropertyPath, null)  } )
                .thenByDescending( (item)=>{ return item.itLoadRate } )
                .thenByDescending( (item)=>{ return item.wbt } ).toArray();
                
            index = oneHourSimilar.findIndex( 
                ( item ) => {
                    return ( item.tsdate == oneHourData.tsdate ) && ( item.recordpointId == oneHourData.recordpointId ) ;  
                }
            );
            
            index = index + 1;
            
            let size = oneHourSimilar.length;
            
            let ranking = ( ( new Big( index ) ).div( new Big(size) ) * 100 ).toFixed(2);
            
            let recordpointId = oneHourData.recordpointId ? oneHourData.recordpointId : 0 ;
            let time = formatDate( oneHourData.tsdate + 3600000*recordpointId , "yyyy-MM-dd HH:mm:ss" );
    
            hourDataSimilarWorkConditionRanks.push( 
                {
                    "rank" : ranking,
                    "time" : time
                }
            );
        }
    }
    
    return hourDataSimilarWorkConditionRanks;
}

export { massagePueData, massageSubsysData, hourDataSimilarRanking };